import { Component, Inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormField, MatHint } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';

import { FeedbackModalData, FeedbackRequest } from 'src/app/modules/core/models/feedback.model';
import { AdobeAnalyticsService } from 'src/app/modules/core/services/adobe-analytics.service';
import { AuthService } from 'src/app/modules/core/services/auth.service';
import { FeedbackService } from 'src/app/modules/core/services/feedback.service';

import { SpinnerComponent } from 'src/app/modules/shared/components/spinner/spinner.component';
import { InteractionContext, PageSection } from 'src/app/modules/shared/constants/adobe-analytics-fields';
import { getCtaClickProps, getPageLoadEvars } from 'src/app/modules/shared/utilities/adobe-analytics.utils';

export const FEEDBACK_MAX_CHARS = 1000;

const URL_PATTERN = /(https?:\/\/|www\.)[^\s]+/i;

function noLinksValidator(control: AbstractControl): ValidationErrors | null {
	const value = control.value as string;
	if (!value) return null;
	return URL_PATTERN.test(value) ? { containsLink: true } : null;
}

interface RatingOption {
	value: number;
	emoji: string;
	label: string;
}

export const RATING_OPTIONS: RatingOption[] = [
	{ value: 1, emoji: '\uD83D\uDE21', label: 'Poor' },      // U+1F621
	{ value: 2, emoji: '\u2639\uFE0F',   label: 'Fair' },       // U+2639 + VS16 (forces emoji presentation)
	{ value: 3, emoji: '\uD83D\uDE10', label: 'Neutral' },    // U+1F610
	{ value: 4, emoji: '\uD83D\uDE42', label: 'Good' },       // U+1F642
	{ value: 5, emoji: '\uD83D\uDE0A', label: 'Excellent' },  // U+1F60A
];

@Component({
	selector: 'cp-feedback-modal',
	templateUrl: './feedback-modal.component.html',
	styleUrls: ['./feedback-modal.component.scss'],
	imports: [
		MatButton,
		MatDialogActions,
		MatDialogContent,
		MatDialogTitle,
		MatFormField,
		MatHint,
		MatIcon,
		MatIconButton,
		MatInput,
		MatTooltip,
		ReactiveFormsModule,
		SpinnerComponent
	]
})
export class FeedbackModalComponent {
	readonly maxChars = FEEDBACK_MAX_CHARS;
	readonly ratingOptions = RATING_OPTIONS;

	form = new FormGroup({
		ratingQues1: new FormControl<number>(0),
		ratingQues2: new FormControl<number>(0),
		feedbackText: new FormControl<string>('', { validators: [noLinksValidator] }),
	});

	isSubmitting = false;

	isPreview = false;

	ratingQues1Touched = false;
	ratingQues2Touched = false;

	get ratingQues1(): number {
		return this.form.value.ratingQues1 ?? 0;
	}

	get ratingQues2(): number {
		return this.form.value.ratingQues2 ?? 0;
	}

	get feedbackText(): string {
		return this.form.value.feedbackText ?? '';
	}

	get showQues1Error(): boolean {
		return this.ratingQues1Touched && this.ratingQues1 === 0;
	}

	get showQues2Error(): boolean {
		return this.ratingQues2Touched && this.ratingQues2 === 0;
	}

	get hasLinkError(): boolean {
		return !!this.form.get('feedbackText')?.errors?.['containsLink'];
	}

	get isSubmitDisabled(): boolean {
		return this.isPreview || this.ratingQues1 === 0 || this.ratingQues2 === 0 || this.isOverLimit || this.hasLinkError || this.isSubmitting;
	}

	get isOverLimit(): boolean {
		return this.feedbackText.length > this.maxChars;
	}

	get charCount(): number {
		return this.feedbackText.length;
	}

	constructor(
		public dialogRef: MatDialogRef<FeedbackModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: FeedbackModalData,
		private authService: AuthService,
		private feedbackService: FeedbackService,
		private adobeAnalyticsService: AdobeAnalyticsService
	) {
		dialogRef.disableClose = true;
		this.isPreview = this.authService.isPreviewUser();
	}

	selectRating(question: 'ratingQues1' | 'ratingQues2', value: number): void {
		this.form.get(question)?.setValue(value);
	}

	onRatingGroupBlur(event: FocusEvent, question: 'ratingQues1' | 'ratingQues2'): void {
		const relatedTarget = event.relatedTarget as Node | null;
		const currentTarget = event.currentTarget as HTMLElement;

		if (!currentTarget.contains(relatedTarget)) {
			if (question === 'ratingQues1') {
				this.ratingQues1Touched = true;
			} else {
				this.ratingQues2Touched = true;
			}
		}
	}

	onClose(): void {
		if (this.isSubmitting) {
			return;
		}
		const evar = getPageLoadEvars(this.data.pageName);
		const prop = getCtaClickProps(InteractionContext.FeedbackSubmission, PageSection.CustomerFeedbackModal, 'Exit');
		this.adobeAnalyticsService.trackCtaClick(evar, prop);
		this.dialogRef.close();
	}

	onCancel(): void {
		if (this.isSubmitting) {
			return;
		}
		const evar = getPageLoadEvars(this.data.pageName);
		const prop = getCtaClickProps(InteractionContext.FeedbackSubmission, PageSection.CustomerFeedbackModal, 'Cancel');
		this.adobeAnalyticsService.trackCtaClick(evar, prop);
		this.dialogRef.close();
	}

	onSubmit(): void {
		if (this.isSubmitDisabled) {
			return;
		}

		const evar = getPageLoadEvars(this.data.pageName);
		const prop = getCtaClickProps(InteractionContext.FeedbackSubmission, PageSection.CustomerFeedbackModal, 'Submit Feedback');
		this.adobeAnalyticsService.trackCtaClick(evar, prop);

		this.isSubmitting = true;

		const payload: FeedbackRequest = {
			marketId: this.data.marketId,
			financialCommunityId: this.data.financialCommunityId,
			salesAgreementId: this.data.salesAgreementId,
			portalExperience: this.data.portalExperience,
			userType: this.data.userType,
			pageUrl: this.data.pageUrl,
			ratingQues1: this.ratingQues1,
			ratingQues2: this.ratingQues2,
			feedbackText: this.feedbackText,
		};

		this.feedbackService.submitFeedback(payload).subscribe({
			next: () => {
				this.isSubmitting = false;
				this.dialogRef.close('submitted');
			},
			error: () => {
				this.isSubmitting = false;
				this.dialogRef.close('error');
			}
		});
	}
}